import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, ArrowLeft, MapPin, ShieldCheck } from "lucide-react";
import { ImageUpload } from "../components/ImageUpload";
import { getMyCourier, courierApply, openParcels, makeParcelOffer } from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };
const inp = "w-full rounded-xl px-4 h-11 text-sm outline-none";

export default function CourierSpace() {
  const nav = useNavigate();
  const [courier, setCourier] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bids, setBids] = useState({});
  const [k, setK] = useState({ full_name: "", phone: "", address: "", birthdate: "", vehicle: "voiture", id_doc_url: "", selfie_url: "" });
  const setKf = (p) => setK((s) => ({ ...s, ...p }));

  const refresh = () => getMyCourier().then((c) => {
    setCourier(c);
    if (c?.status === "approved") openParcels().then(setJobs).catch(() => {});
  }).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const apply = async () => {
    if (!k.full_name || !k.phone || !k.address) { toast.error("Nom, téléphone et adresse requis"); return; }
    setBusy(true);
    try { await courierApply(k); toast.success("Demande envoyée — en attente de validation"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  const offer = async (job, price) => {
    setBusy(true);
    try { await makeParcelOffer(job.id, parseFloat(price)); toast.success("Proposition envoyée (relais TULUSS)"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };

  const status = courier?.status;
  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="courier-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm font-bold mb-4" style={{ color: GREEN }}><ArrowLeft size={18} /> Retour</button>
      <div className="flex items-center gap-2 mb-1"><Truck size={24} color={GREEN} /><h1 className="text-2xl font-extrabold" style={{ color: GREEN }}>Espace livreur</h1></div>

      {status !== "approved" ? (
        <div className="space-y-3 mt-4" data-testid="courier-kyc-form">
          <div className="rounded-xl p-3 flex items-center gap-2" style={{ background: status === "pending" ? "#fff7ed" : status === "rejected" ? "#fef2f2" : "#eafff6" }}>
            <ShieldCheck size={18} color={GREEN} />
            <p className="text-sm font-bold" style={{ color: "#334155" }}>
              {status === "pending" ? "Votre dossier est en cours de vérification." : status === "rejected" ? `Dossier refusé${courier?.status_reason ? " : " + courier.status_reason : ""}. Vous pouvez re-soumettre.` : "Vérification d'identité obligatoire avant de livrer (anti fraude / trafic)."}
            </p>
          </div>
          <input data-testid="kyc-name" value={k.full_name} onChange={(e) => setKf({ full_name: e.target.value })} placeholder="Nom complet" className={inp} style={field} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="kyc-phone" value={k.phone} onChange={(e) => setKf({ phone: e.target.value })} placeholder="Téléphone" className={inp} style={field} />
            <input data-testid="kyc-birthdate" value={k.birthdate} onChange={(e) => setKf({ birthdate: e.target.value })} placeholder="Date de naissance" className={inp} style={field} />
          </div>
          <input data-testid="kyc-address" value={k.address} onChange={(e) => setKf({ address: e.target.value })} placeholder="Adresse" className={inp} style={field} />
          <select data-testid="kyc-vehicle" value={k.vehicle} onChange={(e) => setKf({ vehicle: e.target.value })} className={inp} style={field}>
            <option value="velo">Vélo / scooter</option><option value="voiture">Voiture</option><option value="van">Camionnette</option>
          </select>
          <p className="text-xs font-bold" style={{ color: "#64748b" }}>Pièce d’identité</p>
          <ImageUpload testid="kyc-id" value={k.id_doc_url} onChange={(url) => setKf({ id_doc_url: url })} />
          <p className="text-xs font-bold" style={{ color: "#64748b" }}>Selfie</p>
          <ImageUpload testid="kyc-selfie" value={k.selfie_url} onChange={(url) => setKf({ selfie_url: url })} />
          <button data-testid="kyc-submit" onClick={apply} disabled={busy} className="w-full rounded-xl font-bold text-white h-12" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>{status === "rejected" ? "Re-soumettre" : "Soumettre mon dossier"}</button>
        </div>
      ) : (
        <div className="space-y-3 mt-4" data-testid="courier-marketplace">
          <p className="text-sm" style={{ color: "#64748b" }}>✅ Vérifié. Courses disponibles entre particuliers (contact de l’expéditeur masqué jusqu’au paiement).</p>
          {jobs.length === 0 && <p className="text-center" style={{ color: "#64748b" }}>Aucune course disponible.</p>}
          {jobs.map((j) => (
            <div key={j.id} data-testid={`job-${j.id}`} className="rounded-2xl p-4" style={{ border: "1px solid #f0f0f0" }}>
              <p className="font-bold flex items-center gap-1" style={{ color: GREEN }}><MapPin size={15} /> {j.pickup_address} → {j.dropoff_address}</p>
              <p className="text-xs mt-1" style={{ color: "#64748b" }}>{j.weight_kg} kg · {j.size_label || "—"} · {j.distance_km} km · {j.service === "express" ? "Express" : "Programmé"}</p>
              {j.photo_url && <img src={j.photo_url} alt="" className="w-full h-28 object-cover rounded-xl mt-2" />}
              <p className="text-sm mt-2">Prix proposé : <strong style={{ color: ORANGE }}>CHF {Number(j.proposed_price_chf || 0).toFixed(2)}</strong></p>
              <div className="flex items-center gap-2 mt-2">
                <button data-testid={`job-accept-${j.id}`} onClick={() => offer(j, j.proposed_price_chf)} className="rounded-lg font-bold text-white px-3 h-9 text-sm" style={{ background: GREEN }}>Accepter ce prix</button>
                <input data-testid={`job-bid-${j.id}`} value={bids[j.id] || ""} onChange={(e) => setBids((s) => ({ ...s, [j.id]: e.target.value }))} inputMode="decimal" placeholder="Mon prix" className="w-24 rounded-lg px-2 h-9 text-sm outline-none" style={field} />
                <button data-testid={`job-counter-${j.id}`} onClick={() => bids[j.id] && offer(j, bids[j.id])} className="rounded-lg font-bold px-3 h-9 text-sm" style={field}>Proposer</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
